import { useEffect, useState, Fragment } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  AlertTriangle,
  Clock,
  CheckCircle,
  ChevronRight,
  Upload,
  Milestone,
  ListChecks,
  Database,
} from "lucide-react";
import { Modal, ModalFooterButtons } from "../../components/Modal";
import type {
  PoamMilestone,
  PoamTodoData,
  PoamTodoItem,
} from "../../types/dashboard";
import { API_URL } from "../../../constants/index";
import { getAPI } from "../../../api/frameworksApi";

interface DashboardEnvelope<T> {
  status: string;
  apiVersion: string;
  requestId: string;
  timestamp: string;
  data: T;
  pagination: unknown;
  errors: unknown;
}

interface PoamApiTask {
  poamItemId: string;
  poamId: string;
  controlId: string;
  controlTitle: string;
  title: string;
  description: string;
  status: "Overdue" | "At Risk" | "On Track" | "Pending";
  poamItemStatus: string;
  scheduledCompletionDate: string | null;
  daysRemaining: number | null;
  remediationPlanTitle: string | null;
  remediationPlanDescription: string | null;
  milestones: string | null;
}

interface PoamApiData {
  tasks: PoamApiTask[];
  totalOpen: number;
  overdue: number;
  atRisk: number;
  onTrack: number;
  pending: number;
}

const statusConfig: Record<
  PoamTodoItem["status"],
  { label: string; dot: string; badge: string; border: string }
> = {
  overdue: {
    label: "Overdue",
    dot: "bg-red-500 animate-pulse",
    badge: "bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400",
    border: "border-l-red-500",
  },
  "at-risk": {
    label: "At Risk",
    dot: "bg-amber-500",
    badge:
      "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400",
    border: "border-l-amber-500",
  },
  "on-track": {
    label: "On Track",
    dot: "bg-emerald-500",
    badge:
      "bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400",
    border: "border-l-emerald-500",
  },
  pending: {
    label: "Pending",
    dot: "bg-slate-400",
    badge: "bg-slate-100 dark:bg-white/10 text-slate-500 dark:text-white/40",
    border: "border-l-slate-400",
  },
};

const kpiAccent: Record<string, { icon: string; ring: string }> = {
  indigo: {
    icon: "text-indigo-600 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-500/15",
    ring: "border-l-indigo-500",
  },
  red: {
    icon: "text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-500/15",
    ring: "border-l-red-500",
  },
  amber: {
    icon: "text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-500/15",
    ring: "border-l-amber-500",
  },
  emerald: {
    icon: "text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-500/15",
    ring: "border-l-emerald-500",
  },
  slate: {
    icon: "text-slate-500 dark:text-white/40 bg-slate-100 dark:bg-white/10",
    ring: "border-l-slate-400",
  },
};

function normalizePoamStatus(
  status: PoamApiTask["status"],
): PoamTodoItem["status"] {
  switch (status) {
    case "Overdue":
      return "overdue";
    case "At Risk":
      return "at-risk";
    case "On Track":
      return "on-track";
    default:
      return "pending";
  }
}

function parseMilestones(raw: string | null): PoamMilestone[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as Array<{ name?: string; date?: string }>;
    return parsed
      .filter((milestone) => milestone?.name && milestone?.date)
      .map((milestone) => ({
        name: milestone.name as string,
        date: milestone.date as string,
      }));
  } catch {
    return [];
  }
}

function normalizePoamData(data: PoamApiData): PoamTodoData {
  return {
    totalOpen: data.totalOpen ?? 0,
    overdue: data.overdue ?? 0,
    atRisk: data.atRisk ?? 0,
    onTrack: data.onTrack ?? 0,
    pending: data.pending ?? 0,
    items: (data.tasks ?? []).map((task) => ({
      poamId: task.poamId,
      poamItemId: task.poamItemId,
      status: normalizePoamStatus(task.status),
      controlId: task.controlId,
      title: task.title,
      description: task.description,
      controlTitle: task.controlTitle,
      scheduledCompletionDate: task.scheduledCompletionDate,
      daysRemaining: task.daysRemaining,
      poamItemStatus: task.poamItemStatus,
      remediationPlanTitle: task.remediationPlanTitle,
      remediationPlanDescription: task.remediationPlanDescription,
      milestones: parseMilestones(task.milestones),
    })),
  };
}

function formatDate(iso: string | null) {
  if (!iso) return "Not Scheduled";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatStatusLabel(value: string) {
  return value
    .split(/[_-]/g)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function DaysRemainingBadge({ days }: { days: number | null }) {
  if (days == null) {
    return (
      <span className="text-xs text-[#94a3b8] dark:text-white/30">TBD</span>
    );
  }
  const color =
    days < 0
      ? "text-red-600 dark:text-red-400 font-black"
      : days <= 14
        ? "text-amber-600 dark:text-amber-400 font-bold"
        : "text-emerald-600 dark:text-emerald-400 font-bold";
  return (
    <span className={`text-xs tabular-nums ${color}`}>
      {days < 0 ? `${Math.abs(days)}d overdue` : `${days}d`}
    </span>
  );
}

function PanelState({
  title,
  body,
  tone = "neutral",
}: {
  title: string;
  body: string;
  tone?: "neutral" | "error";
}) {
  const isError = tone === "error";
  return (
    <div className="rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] bg-white dark:bg-[#131320] px-6 py-10 text-center">
      <div
        className={`mx-auto mb-3 flex size-10 items-center justify-center rounded-full ${
          isError
            ? "bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-400"
            : "bg-slate-100 text-slate-500 dark:bg-white/[0.06] dark:text-white/40"
        }`}
      >
        <Database className="size-4" />
      </div>
      <p className="text-sm font-bold text-[#0f172a] dark:text-white">
        {title}
      </p>
      <p className="mt-1 text-xs text-[#64748b] dark:text-white/40">{body}</p>
    </div>
  );
}

export function PoamTodoTab() {
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [confirmItem, setConfirmItem] = useState<PoamTodoItem | null>(null);
  const [data, setData] = useState<PoamTodoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadPoam = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = (await getAPI(
          API_URL.DASHBOARD_POAM,
        )) as DashboardEnvelope<PoamApiData>;
        if (cancelled) return;
        setData(normalizePoamData(response.data));
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load dashboard POA&M data", err);
        setError("Unable to load remediation tasks right now.");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadPoam();

    return () => {
      cancelled = true;
    };
  }, []);

  const kpis = [
    {
      label: "Total Open",
      value: data?.totalOpen ?? 0,
      icon: ListChecks,
      accent: "indigo",
    },
    {
      label: "Overdue",
      value: data?.overdue ?? 0,
      icon: AlertTriangle,
      accent: "red",
      pulse: true,
    },
    {
      label: "At Risk",
      value: data?.atRisk ?? 0,
      icon: Clock,
      accent: "amber",
    },
    {
      label: "On Track",
      value: data?.onTrack ?? 0,
      icon: CheckCircle,
      accent: "emerald",
    },
    {
      label: "Pending",
      value: data?.pending ?? 0,
      icon: Clock,
      accent: "slate",
    },
  ];

  const handleMarkFixed = () => {
    setConfirmItem(null);
  };

  if (loading && !data) {
    return (
      <PanelState
        title="Loading remediation tasks"
        body="Fetching the Platform Owner POA&M dashboard."
      />
    );
  }

  if (error && !data) {
    return <PanelState title="POA&M unavailable" body={error} tone="error" />;
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        {kpis.map((kpi, i) => {
          const Icon = kpi.icon;
          const ac = kpiAccent[kpi.accent];
          return (
            <motion.div
              key={kpi.label}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07, duration: 0.35 }}
              className={`bg-white dark:bg-[#131320] rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] border-l-[3px] ${ac.ring} p-4 hover:shadow-md dark:hover:shadow-black/30 transition-all hover:-translate-y-px`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`size-9 rounded-lg flex items-center justify-center ${ac.icon}`}
                >
                  <Icon className="size-4" />
                </div>
                <div>
                  <p className="text-2xl font-black text-[#0f172a] dark:text-white tracking-tight leading-none">
                    {kpi.value}
                  </p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {"pulse" in kpi && kpi.pulse && (
                      <span className="size-1.5 rounded-full bg-red-500 animate-pulse" />
                    )}
                    <p className="text-xs font-bold text-[#0f172a] dark:text-white/70">
                      {kpi.label}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="bg-white dark:bg-[#131320] rounded-xl border border-[#e2e8f0] dark:border-white/[0.06] overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-[#f1f5f9] dark:border-white/[0.04]">
          <h3 className="text-sm font-bold text-[#0f172a] dark:text-white">
            Remediation Tasks
          </h3>
          <p className="text-[10px] text-[#94a3b8] dark:text-white/30 mt-0.5">
            Sorted by urgency â€” overdue first
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-[#f1f5f9] dark:border-white/[0.04]">
                <th className="px-5 py-2.5 w-8" />
                {[
                  "Priority",
                  "Control",
                  "Title",
                  "Asset / System",
                  "Due Date",
                  "Days Left",
                  "Status",
                ].map((h) => (
                  <th
                    key={h}
                    className="text-center px-3 py-2.5 text-[10px] font-bold tracking-wide text-[#94a3b8] dark:text-white/30"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(data?.items ?? []).length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-5 py-10 text-center text-xs text-[#94a3b8] dark:text-white/30"
                  >
                    No open remediation tasks found.
                  </td>
                </tr>
              ) : (
                (data?.items ?? []).map((item) => {
                  const cfg = statusConfig[item.status];
                  const isExpanded = expandedRow === item.poamItemId;
                  const steps = (item.remediationPlanDescription ?? "")
                    .split("\n")
                    .map((step) => step.trim())
                    .filter(Boolean);

                  return (
                    <Fragment key={item.poamItemId}>
                      <tr
                        onClick={() =>
                          setExpandedRow((prev) =>
                            prev === item.poamItemId ? null : item.poamItemId,
                          )
                        }
                        className={`border-b border-[#f8f9fc] dark:border-white/[0.03] border-l-[3px] ${cfg.border} cursor-pointer transition-colors ${
                          isExpanded
                            ? "bg-[#f8f9fc] dark:bg-white/[0.03]"
                            : "hover:bg-[#f8f9fc] dark:hover:bg-white/[0.02]"
                        }`}
                      >
                        <td className="px-5 py-2.5">
                          <ChevronRight
                            className={`size-3.5 text-[#94a3b8] dark:text-white/30 transition-transform duration-200 ${
                              isExpanded ? "rotate-90" : ""
                            }`}
                          />
                        </td>
                        <td className="px-3 py-2.5">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold ${cfg.badge}`}
                          >
                            <span
                              className={`size-1.5 rounded-full ${cfg.dot}`}
                            />
                            {cfg.label}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 font-mono font-bold text-[#0f172a] dark:text-white/70">
                          {item.controlId?.toUpperCase()}
                        </td>
                        <td className="px-3 py-2.5 font-medium text-[#0f172a] dark:text-white/80 max-w-[260px] truncate">
                          {item.title}
                        </td>
                        <td className="px-3 py-2.5 text-[#64748b] dark:text-white/40">
                          {item.controlTitle}
                        </td>
                        <td className="px-3 py-2.5 text-[#64748b] dark:text-white/40 whitespace-nowrap">
                          {formatDate(item.scheduledCompletionDate)}
                        </td>
                        <td className="px-3 py-2.5">
                          <DaysRemainingBadge days={item.daysRemaining} />
                        </td>
                        <td className="px-3 py-2.5 text-[#64748b] dark:text-white/40">
                          {formatStatusLabel(item.poamItemStatus)}
                        </td>
                      </tr>

                      <AnimatePresence>
                        {isExpanded && (
                          <tr>
                            <td colSpan={8} className="p-0">
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.25 }}
                                className="overflow-hidden"
                              >
                                <div className="px-8 py-4 bg-[#f1f5f9]/50 dark:bg-white/[0.015] space-y-4">
                                  <div>
                                    <h4 className="text-[10px] font-bold tracking-wide text-[#94a3b8] dark:text-white/30 mb-2">
                                      Remediation Steps
                                    </h4>
                                    {steps.length > 0 ? (
                                      <ol className="space-y-1.5">
                                        {steps.map((step, i) => (
                                          <li
                                            key={i}
                                            className="flex items-start gap-2 text-xs text-[#0f172a] dark:text-white/70"
                                          >
                                            <span className="text-[10px] font-black text-indigo-500 dark:text-indigo-400 mt-px flex-shrink-0">
                                              {i + 1}.
                                            </span>
                                            <span>
                                              {step.replace(/^\d+\.\s*/, "")}
                                            </span>
                                          </li>
                                        ))}
                                      </ol>
                                    ) : (
                                      <p className="text-xs text-[#94a3b8] dark:text-white/30">
                                        No remediation plan provided yet.
                                      </p>
                                    )}
                                  </div>

                                  {item.milestones.length > 0 && (
                                    <div>
                                      <h4 className="text-[10px] font-bold tracking-wide text-[#94a3b8] dark:text-white/30 mb-2">
                                        Milestones
                                      </h4>
                                      <div className="flex items-center gap-1 overflow-x-auto">
                                        {item.milestones.map((ms, i) => (
                                          <Fragment
                                            key={`${ms.name}-${ms.date}-${i}`}
                                          >
                                            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white dark:bg-[#131320] border border-[#e2e8f0] dark:border-white/[0.06] flex-shrink-0">
                                              <Milestone className="size-3 text-indigo-500 dark:text-indigo-400" />
                                              <div>
                                                <p className="text-[10px] font-bold text-[#0f172a] dark:text-white/70">
                                                  {ms.name}
                                                </p>
                                                <p className="text-[9px] text-[#94a3b8] dark:text-white/30">
                                                  {formatDate(ms.date)}
                                                </p>
                                              </div>
                                            </div>
                                            {i < item.milestones.length - 1 && (
                                              <div className="w-6 h-px bg-[#e2e8f0] dark:bg-white/[0.08] flex-shrink-0" />
                                            )}
                                          </Fragment>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </motion.div>
                            </td>
                          </tr>
                        )}
                      </AnimatePresence>
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      <Modal
        open={!!confirmItem}
        onClose={() => setConfirmItem(null)}
        title="Mark as Fixed"
        description={`Are you sure you want to mark "${confirmItem?.title}" as remediated?`}
        size="sm"
        footer={
          <ModalFooterButtons
            onCancel={() => setConfirmItem(null)}
            onSubmit={handleMarkFixed}
            submitLabel="Confirm"
          />
        }
      >
        <p className="text-xs text-[#64748b] dark:text-white/40">
          This will transition the item to <strong>closed_remediated</strong>{" "}
          status. This action can be reversed by an administrator.
        </p>
      </Modal>
    </div>
  );
}
